import React, { useState, useRef, useEffect } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Controller } from "react-hook-form";

interface LocationInputProps {
  name: string;
  control: any;
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
  rules?: any;
}

export function LocationInput({
  name,
  control,
  onPlaceSelect,
  rules,
}: LocationInputProps) {
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const error = fieldState.error?.message;
        const fetchSuggestions = async (inputValue: string) => {
          if (!inputValue || !places?.AutocompleteSuggestion) {
            setPredictions([]);
            return;
          }
          try {
            const { suggestions } =
              await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: inputValue,
                language: "en",
                // Restrict results strictly to the bounding box of the continental United States.
                // Coordinates define the southwest and northeast corners of the box:
                locationRestriction: {
                  south: 24.396308,
                  west: -125.0,
                  north: 49.384358,
                  east: -66.93457,
                },
              });
            setPredictions(suggestions || []);
          } catch (err) {
            console.error("Autocomplete fetch error:", err);
            setPredictions([]);
          }
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          field.onChange(value);

          // debounce calls to fetchSuggestions
          if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
          debounceTimeout.current = setTimeout(() => {
            fetchSuggestions(value);
          }, 300); // wait 300ms after last keystroke
        };

        const handleSelect = async (placeId: string) => {
          const prediction = predictions.find(
            (p) => p.placePrediction?.placeId === placeId
          );
          if (!prediction) return;

          try {
            const place = prediction.placePrediction!.toPlace();
            await place.fetchFields({
              fields: ["formattedAddress", "viewport", "displayName"],
            });

            const formatted = place.formattedAddress || "";
            field.onChange(formatted);
            onPlaceSelect(place);
            setPredictions([]);
          } catch (err) {
            console.error("Place fetch error:", err);
          }
        };

        return (
          <div style={{ position: "relative", marginBottom: "1rem" }}>
            <label htmlFor="place-input">Search for Parking Near</label>
            <input
              id="place-input"
              type="text"
              value={field.value || ""}
              onChange={handleChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Address or Zip Code"
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "1rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxSizing: "border-box",
              }}
              autoComplete="off"
            />
            {error && <p style={{ color: "red", marginTop: "4px" }}>{error}</p>}
            {predictions.length > 0 && (
              <ul
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #ccc",
                  zIndex: 1000,
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {predictions.map((pred) => (
                  <li
                    key={pred.placePrediction?.placeId}
                    onClick={() => handleSelect(pred.placePrediction!.placeId)}
                    style={{
                      padding: "8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {pred.placePrediction!.text?.text ||
                      pred.placePrediction!.mainText?.text ||
                      "Unnamed place"}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }}
    />
  );
}
